import React from 'react';
import { MandatoryTopicReminderType } from '../../../../../../generated/core-api/graphql-react-query';
import { SessionComplianceChecklistItem } from './type';
import useTriggerReminder from './useTriggerReminder';

export default function useComplianceReminder(sessionInProgress: boolean, sessionTopics: SessionComplianceChecklistItem[]) {
  const triggerReminder = useTriggerReminder();
  const [intervalIds, setIntervalIds] = React.useState<{ intervalId: NodeJS.Timeout, topicTextLower: string }[]>([]);

  const addInterval = React.useCallback((topic: SessionComplianceChecklistItem) => {
    if (!topic.reminder.type) {
      throw new Error('Reminder type is not defined');
    }
    const existingInterval = intervalIds.find((item) => item.topicTextLower === topic.topic.textLower);
    if (!existingInterval) {
      const intervalMethod = topic.reminder.type === MandatoryTopicReminderType.FixedTime ? setTimeout : setInterval;
      const delayUntilReminder = topic.reminder.time * 60 * 1000;
      const interval = intervalMethod(() => { triggerReminder(topic); }, delayUntilReminder);
      setIntervalIds((prev) => [...prev, { intervalId: interval, topicTextLower: topic.topic.textLower, type: topic.reminder.type }]);
    }
  }, [intervalIds]);

  const removeInterval = React.useCallback((intervalId: NodeJS.Timeout) => {
    clearInterval(intervalId);
    setIntervalIds((prev) => prev.filter((item) => item.intervalId !== intervalId));
  }, []);

  React.useEffect(() => {
    if (!sessionInProgress) {
      intervalIds.forEach((item) => removeInterval(item.intervalId));
      return;
    }

    sessionTopics.forEach((topic) => {
      if (topic.reminder && topic.requiredAt) {
        if (topic.isCompleted) {
          const intervalId = intervalIds.find((item) => item.topicTextLower === topic.topic.textLower);
          if (intervalId) {
            removeInterval(intervalId.intervalId);
          }
        } else {
          addInterval(topic);
        }
      }
    });
  }, [sessionInProgress, sessionTopics]);
}
